import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

// Minimal controlled slider (single or range) built for DiamondListPage only.
// Every interactive state (hover/focus/drag) is styled via CSS so it can
// follow the per-store `--color-theme` variable, unlike rc-slider which
// hardcodes its own accent color on those states.
const DpSlider = ({
    min = 0,
    max,
    step = 1,
    value,
    onChange,
    marks,
    range = false,
    allowCross = true,
    neutralTrack = false,
    ariaLabel,
}) => {
    const railRef = useRef(null);
    const [draggingHandle, setDraggingHandle] = useState(null);

    const values = range ? value : [value];

    const percentForValue = useCallback(
        (v) => ((v - min) / (max - min)) * 100,
        [min, max]
    );

    const stepFromClientX = useCallback((clientX) => {
        const rect = railRef.current.getBoundingClientRect();
        const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
        const raw = min + ratio * (max - min);
        return clamp(Math.round(raw / step) * step, min, max);
    }, [min, max, step]);

    const commitValue = useCallback((handleIndex, nextStep) => {
        if (!range) {
            onChange(nextStep);
            return;
        }
        const next = [...value];
        if (handleIndex === 0) {
            next[0] = allowCross ? nextStep : Math.min(nextStep, next[1]);
        } else {
            next[1] = allowCross ? nextStep : Math.max(nextStep, next[0]);
        }
        onChange(next);
    }, [range, value, allowCross, onChange]);

    const moveNearestHandle = useCallback((nextStep) => {
        if (!range) {
            onChange(nextStep);
            return;
        }
        const [low, high] = value;
        const handleIndex = Math.abs(nextStep - low) <= Math.abs(nextStep - high) ? 0 : 1;
        commitValue(handleIndex, nextStep);
    }, [range, value, onChange, commitValue]);

    useEffect(() => {
        if (draggingHandle === null) return undefined;
        const onMove = (e) => {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            commitValue(draggingHandle, stepFromClientX(clientX));
        };
        const onUp = () => setDraggingHandle(null);
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    }, [draggingHandle, commitValue, stepFromClientX]);

    const handlePointerDown = (handleIndex) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggingHandle(handleIndex);
    };

    const handleRailPointerDown = (e) => {
        moveNearestHandle(stepFromClientX(e.clientX));
    };

    const handleKeyDown = (handleIndex) => (e) => {
        const current = range ? value[handleIndex] : value;
        let nextStep;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') nextStep = clamp(current + step, min, max);
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') nextStep = clamp(current - step, min, max);
        else if (e.key === 'Home') nextStep = min;
        else if (e.key === 'End') nextStep = max;
        else return;
        e.preventDefault();
        commitValue(handleIndex, nextStep);
    };

    const stepCount = Math.round((max - min) / step);
    const markEntries = useMemo(() => {
        if (!marks) return [];
        return Array.from({ length: stepCount + 1 }, (_, i) => min + i * step)
            .filter((v) => marks[v] !== undefined)
            .map((v) => ({ value: v, label: marks[v] }));
    }, [marks, min, step, stepCount]);

    const trackStyle = range
        ? {
            left: `${percentForValue(values[0])}%`,
            width: `${percentForValue(values[1]) - percentForValue(values[0])}%`,
        }
        : { left: '0%', width: `${percentForValue(values[0])}%` };

    return (
        <div className="dp-rangeslider">
            <div
                className="dp-rangeslider-rail"
                ref={railRef}
                onPointerDown={handleRailPointerDown}
            >
                <div
                    className={`dp-rangeslider-track${neutralTrack ? ' dp-rangeslider-track--neutral' : ''}`}
                    style={trackStyle}
                />
                {values.map((v, i) => (
                    <div
                        key={i}
                        className="dp-rangeslider-handle"
                        style={{ left: `${percentForValue(v)}%` }}
                        role="slider"
                        tabIndex={0}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={v}
                        aria-label={ariaLabel}
                        onPointerDown={handlePointerDown(i)}
                        onKeyDown={handleKeyDown(i)}
                    />
                ))}
            </div>
            {markEntries.length > 0 && (
                <div className="dp-rangeslider-marks">
                    {markEntries.map(({ value: v, label }) => (
                        <span
                            key={v}
                            className="dp-rangeslider-mark-text"
                            style={{ left: `${percentForValue(v)}%` }}
                            onClick={() => moveNearestHandle(v)}
                        >
                            {label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DpSlider;
